import {
  Box,
  Flex,
  HStack,
  Link,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { ReactNode, useMemo } from "react";
import { ThemeColor } from "../../theme/palette";
import { KyrosToken } from "../../rest/kairos/types";
import { formatAddress, formatSmartFixed6 } from "../../utils/format";
import { aggregateDripDepositors, RawDripPosition } from "./aggregateDripDepositors";
import { resolveRewardTokenMeta } from "./resolveRewardTokenMeta";
import { PartnerTokenIcon } from "./PartnerTokenIcon";
import { DRIP_BOX_BG, DRIP_BORDER, DRIP_TEXT_MUTED } from "./dripStyles";
import { useDripVaults } from "../../hooks/useDripVaults";
import { findVaultById } from "../../constants/dripVaults";

interface DripDepositorsSectionProps {
  positions: RawDripPosition[];
  kyrosTokens: KyrosToken[];
  isLoading: boolean;
}

// How many depositors to list before collapsing the rest into a summary line.
const TOP_DEPOSITORS_LIMIT = 15;

const Card = ({
  title,
  meta,
  children,
}: {
  title: string;
  meta: string;
  children: ReactNode;
}) => (
  <Box
    bg={DRIP_BOX_BG}
    borderRadius="xl"
    border="1px solid"
    borderColor={DRIP_BORDER}
    overflow="hidden"
    w="100%"
  >
    <Flex
      justify="space-between"
      align="center"
      px={{ base: 5, md: 6 }}
      py={5}
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
    >
      <Text fontSize="lg" fontWeight="semibold" color="white">
        {title}
      </Text>
      <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
        {meta}
      </Text>
    </Flex>
    {children}
  </Box>
);

const ProgressBar = ({ pct }: { pct: number }) => (
  <Box w="100%" maxW="120px" h="3px" bg="whiteAlpha.100" borderRadius="full" overflow="hidden">
    <Box h="100%" w={`${Math.min(100, Math.max(0, pct))}%`} bg={ThemeColor.KAIROS} opacity={0.7} />
  </Box>
);

const solAmount = (value: number) => `${formatSmartFixed6(value)} SOL`;

export const DripDepositorsSection = ({
  positions,
  kyrosTokens,
  isLoading,
}: DripDepositorsSectionProps) => {
  const dripVaults = useDripVaults();
  const vaultSymbol = (vaultId: string) =>
    findVaultById(dripVaults, vaultId)?.symbol ?? vaultId;

  const kyrosByMint = useMemo(
    () =>
      kyrosTokens.reduce<Record<string, KyrosToken>>((acc, token) => {
        if (!acc[token.id]) acc[token.id] = token;
        return acc;
      }, {}),
    [kyrosTokens]
  );

  const stats = useMemo(
    () => aggregateDripDepositors(positions),
    [positions]
  );

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch" w="100%">
        <Skeleton height="200px" borderRadius="xl" />
        <Skeleton height="320px" borderRadius="xl" />
      </VStack>
    );
  }

  if (stats.depositorCount === 0) {
    return (
      <Box
        bg={DRIP_BOX_BG}
        borderRadius="xl"
        border="1px solid"
        borderColor={DRIP_BORDER}
        px={6}
        py={8}
      >
        <Text fontSize="sm" color={DRIP_TEXT_MUTED}>
          No Drip positions on-chain yet.
        </Text>
      </Box>
    );
  }

  const topTokenSol = stats.tokenBreakdown[0]?.solValue ?? 0;
  const topDepositorSol = stats.depositors[0]?.solValue ?? 0;
  const shownDepositors = stats.depositors.slice(0, TOP_DEPOSITORS_LIMIT);

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {/* Reward token breakdown */}
      <Card
        title="Reward tokens"
        meta={`${stats.tokenCount} token${stats.tokenCount > 1 ? "s" : ""} dripped`}
      >
        <TableContainer px={{ base: 5, md: 6 }} pb={2}>
          <Table variant="info" sx={{ "tbody td": { py: 4 } }}>
            <Thead>
              <Tr>
                <Th>Token</Th>
                <Th isNumeric>Depositors</Th>
                <Th isNumeric>SOL dripped</Th>
              </Tr>
            </Thead>
            <Tbody>
              {stats.tokenBreakdown.map((token) => {
                const meta = resolveRewardTokenMeta(token.mint, kyrosByMint);
                const barWidth = topTokenSol > 0 ? (token.solValue / topTokenSol) * 100 : 0;
                return (
                  <Tr
                    key={token.mint}
                    sx={{
                      _hover: { backgroundColor: "whiteAlpha.100" },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Td verticalAlign="middle">
                      <HStack spacing={2.5} align="center">
                        <PartnerTokenIcon
                          sources={meta.iconSources}
                          label={meta.symbol}
                          accent={ThemeColor.KAIROS}
                          boxSize="24px"
                        />
                        <Link
                          href={`https://solscan.io/token/${token.mint}`}
                          isExternal
                          color="white"
                          _hover={{ color: ThemeColor.KAIROS }}
                          noOfLines={1}
                        >
                          {meta.symbol}
                        </Link>
                      </HStack>
                    </Td>
                    <Td isNumeric verticalAlign="middle">
                      <Text color={DRIP_TEXT_MUTED}>{token.depositorCount}</Text>
                    </Td>
                    <Td isNumeric verticalAlign="middle">
                      <VStack align="end" spacing={1.5}>
                        <HStack spacing={2} align="baseline">
                          <Text color="white" fontWeight="medium">
                            {formatSmartFixed6(token.solValue)}
                          </Text>
                          <Text fontSize="xs" color={DRIP_TEXT_MUTED} w="42px">
                            {token.vaultSharePct.toFixed(1)}%
                          </Text>
                        </HStack>
                        <ProgressBar pct={barWidth} />
                      </VStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Card>

      {/* Top depositors */}
      <Card
        title="Top depositors"
        meta={`${stats.depositorCount} position${stats.depositorCount > 1 ? "s" : ""} · ${solAmount(
          stats.totalSolValue
        )}`}
      >
        <TableContainer px={{ base: 5, md: 6 }} pb={2}>
          <Table variant="info" sx={{ "tbody td": { py: 4 } }}>
            <Thead>
              <Tr>
                <Th>Depositor</Th>
                <Th>Vault</Th>
                <Th>Reward token</Th>
                <Th isNumeric>Position</Th>
              </Tr>
            </Thead>
            <Tbody>
              {shownDepositors.map((depositor, index) => {
                const meta = resolveRewardTokenMeta(depositor.rewardTokenMint, kyrosByMint);
                const barWidth =
                  topDepositorSol > 0 ? (depositor.solValue / topDepositorSol) * 100 : 0;
                return (
                  <Tr
                    key={`${depositor.vaultId}-${depositor.owner}`}
                    sx={{
                      _hover: { backgroundColor: "whiteAlpha.100" },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Td verticalAlign="middle">
                      <HStack spacing={2.5} align="center">
                        <Text
                          fontSize="xs"
                          color={DRIP_TEXT_MUTED}
                          w="16px"
                          textAlign="right"
                          flexShrink={0}
                        >
                          {index + 1}
                        </Text>
                        <Link
                          href={`https://solscan.io/account/${depositor.owner}`}
                          isExternal
                          color="white"
                          fontFamily="mono"
                          fontSize="sm"
                          _hover={{ color: ThemeColor.KAIROS }}
                        >
                          {formatAddress(depositor.owner)}
                        </Link>
                      </HStack>
                    </Td>
                    <Td verticalAlign="middle">
                      <Text color={DRIP_TEXT_MUTED} noOfLines={1}>
                        {vaultSymbol(depositor.vaultId)}
                      </Text>
                    </Td>
                    <Td verticalAlign="middle">
                      <HStack spacing={2} align="center">
                        <PartnerTokenIcon
                          sources={meta.iconSources}
                          label={meta.symbol}
                          accent={ThemeColor.KAIROS}
                          boxSize="20px"
                        />
                        <Text color={DRIP_TEXT_MUTED} noOfLines={1}>
                          {meta.symbol}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric verticalAlign="middle">
                      <VStack align="end" spacing={1.5}>
                        <HStack spacing={2} align="baseline">
                          <Text color="white" fontWeight="medium">
                            {formatSmartFixed6(depositor.solValue)}
                          </Text>
                          <Text fontSize="xs" color={DRIP_TEXT_MUTED} w="42px">
                            {depositor.vaultSharePct.toFixed(1)}%
                          </Text>
                        </HStack>
                        <ProgressBar pct={barWidth} />
                      </VStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {stats.depositorCount > TOP_DEPOSITORS_LIMIT && (
          <Text fontSize="xs" color={DRIP_TEXT_MUTED} px={{ base: 5, md: 6 }} pb={5} pt={1}>
            Showing top {TOP_DEPOSITORS_LIMIT} of {stats.depositorCount} positions.
          </Text>
        )}
      </Card>
    </VStack>
  );
};
